import type { QuizAnswer } from '../types/database';
import type { Question, ScoringTier } from '../types/quiz';

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function calculateQuestionScore(
  isCorrect: boolean,
  timeTakenMs: number,
  timeMaxSeconds: number
): number {
  if (!isCorrect) return 0;

  const timeTakenSeconds = timeTakenMs / 1000;
  const ratio = clamp(timeTakenSeconds / timeMaxSeconds, 0, 1);
  const score = 5 + 5 * (1 - ratio); // 5..10 for correct

  return score;
}

export function calculateRawScore(answers: QuizAnswer[], questions: Question[], timeMaxSeconds: number): number {
  let rawScore = 0;

  answers.forEach((answer) => {
    const question = questions.find((q) => q.id === answer.question_id);
    if (question) {
      const questionScore = calculateQuestionScore(
        answer.is_correct,
        answer.time_taken_ms,
        timeMaxSeconds
      );
      rawScore += questionScore;
    }
  });

  return rawScore;
}

/**
 * Dynamic AI IQ calculation.
 *
 * Assumptions:
 * - Each correct answer scores between 5 (slow) and 10 (fast).
 * - For N questions:
 *    - All correct but slow  => raw = N * 5
 *    - All correct and fast => raw = N * 10
 *
 * Mapping:
 * - raw in [0, N*5)    -> linearly to [0, 105)
 * - raw = N*5          -> 106  (floor for "all correct but slow")
 * - raw in (N*5, N*10] -> linearly to (106, 150]
 */
export function calculateAiIq(rawScore: number, totalQuestions: number): number {
  if (totalQuestions <= 0) {
    return 0; // no questions, no IQ
  }

  const minFull = totalQuestions * 5;   // all correct, slowest
  const maxFull = totalQuestions * 10;  // all correct, fastest

  // Make sure rawScore stays in valid range
  const clampedRaw = clamp(rawScore, 0, maxFull);

  if (clampedRaw < minFull) {
    // Scale 0..minFull -> 0..105
    return (clampedRaw / minFull) * 105;
  }

  // If you want to keep a hard floor at exactly 106 for "all correct but slow":
  if (Math.abs(clampedRaw - minFull) < 1e-6) {
    return 106;
  }

  // Scale (minFull..maxFull] -> (106..150]
  const span = maxFull - minFull; // = totalQuestions * 5
  return 106 + (clampedRaw - minFull) * (44 / span);
}

export function getAiIqDisplay(aiIq: number): string {
  return Math.round(aiIq).toString();
}

export function getIntelligenceTier(aiIq: number, scoringTiers: ScoringTier[]): string {
  const tier = scoringTiers.find(t => aiIq >= t.minIq && aiIq <= t.maxIq);
  return tier?.intelligenceTier || 'Unknown';
}

export function getInterpretation(aiIq: number, scoringTiers: ScoringTier[]): string {
  const tier = scoringTiers.find(t => aiIq >= t.minIq && aiIq <= t.maxIq);
  return tier?.interpretation || 'Unknown';
}
